// Meta (Facebook) Pixel — consent-gated, exactly like the GA helper.
//
// fbevents.js is NOT added to the page until the user grants consent (see
// cookie_consent_banner.jsx), so no Meta cookies (_fbp, _fbc) are set before
// then. This is the ePrivacy/GDPR-compliant behaviour.
//
// The pixel ID is read from REACT_APP_META_PIXEL_ID. Until a real numeric ID is
// supplied it stays as the placeholder below and every function here is a no-op
// — nothing loads, nothing tracks. The server-side Conversions API (see Rails
// Meta::ConversionsApi) mirrors the key conversions with the same eventID so
// Meta de-duplicates the browser and server copies.
import { getConsent, CONSENT } from './consent'

// TODO: replace with the real Meta Pixel ID (or set REACT_APP_META_PIXEL_ID).
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || 'XXXXXXXXXXXXXXX'

// A real pixel ID is a long run of digits; the placeholder must not activate it.
export const isMetaPixelConfigured = () => /^\d{6,}$/.test(PIXEL_ID)

let initialized = false

// Injects fbevents.js and boots the pixel. Idempotent, no-op until a real ID
// exists. Does not auto-send PageView — the funnel fires its own events.
export const enableMetaPixel = () => {
  if (initialized || !isMetaPixelConfigured() || typeof document === 'undefined') return
  initialized = true

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = !0
    n.version = '2.0'
    n.queue = []
    t = b.createElement(e)
    t.async = !0
    t.src = v
    s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  /* eslint-enable */

  // We loaded the pixel only after explicit consent, so grant Meta's own flag.
  window.fbq('consent', 'grant')
  window.fbq('init', PIXEL_ID)
}

// A fresh id shared by a browser event and its server-side CAPI twin, so Meta
// de-duplicates the two copies of one conversion.
export const newMetaEventId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`

// Fires a Meta *standard* event (ViewContent, Lead, CompleteRegistration, …).
// eventId ties the browser hit to its server-side CAPI twin for dedup.
export const trackMetaEvent = (name, params = {}, eventId = null) => {
  if (!initialized || typeof window.fbq !== 'function') return
  const options = eventId ? { eventID: eventId } : undefined
  window.fbq('track', name, params, options)
}

// Fires a Meta *custom* event. Pass eventId to dedup against a server-side CAPI
// copy of the same event (e.g. EventRegistration).
export const trackMetaCustom = (name, params = {}, eventId = null) => {
  if (!initialized || typeof window.fbq !== 'function') return
  const options = eventId ? { eventID: eventId } : undefined
  window.fbq('trackCustom', name, params, options)
}

// Stops tracking and clears Meta cookies (consent withdrawn).
export const disableMetaPixel = () => {
  initialized = false
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('consent', 'revoke')
  }
  clearMetaCookies()
}

const clearMetaCookies = () => {
  if (typeof document === 'undefined') return
  const host = window.location.hostname
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim()
    if (name === '_fbp' || name === '_fbc') {
      document.cookie = `${name}=; ${expire}`
      document.cookie = `${name}=; ${expire}; domain=${host}`
      document.cookie = `${name}=; ${expire}; domain=.${host}`
    }
  })
}

// On load, honour a decision the user already made — without re-prompting.
if (getConsent() === CONSENT.GRANTED) enableMetaPixel()
